import React, { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { RefreshCw, Sparkles, Radar, Activity, Compass } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import InvestigationCard from "@/components/InvestigationCard";
import TradeOfTheDay from "@/components/TradeOfTheDay";
import { fmtNum, fmtUSD } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import AIQuotaBadge from "@/components/AIQuotaBadge";
import FirstRunChecklist from "@/components/FirstRunChecklist";

function SectionCard({ title, icon: Icon, testid, children, cta }) {
  return (
    <section className="border border-zinc-800 rounded-md p-4" data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex flex-wrap items-center gap-2">
          <Icon size={14} className="text-zinc-400" />
          <h3 className="font-heading font-semibold text-zinc-100">{title}</h3>
        </div>
        {cta}
      </div>
      {children}
    </section>
  );
}

function _briefAgeLabel(ageSec, generatedAt) {
  if (typeof ageSec === "number" && ageSec >= 0) {
    if (ageSec < 60) return "just now";
    const mins = Math.floor(ageSec / 60);
    if (mins < 60) return `${mins} min ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ${mins % 60}m ago`;
    return `${Math.floor(hrs / 24)}d ago`;
  }
  if (generatedAt) {
    try { return new Date(generatedAt).toLocaleString(); } catch { return "recently"; }
  }
  return "recently";
}

export default function Dashboard() {
  const [regenerating, setRegenerating] = useState(false);

  const { data: queue, isLoading: queueLoading } = useQuery({
    queryKey: ["investigation-queue"],
    queryFn: async () => (await api.get("/intel/investigation-queue?limit=8")).data,
    refetchInterval: 90_000,
  });

  const { data: summary } = useQuery({
    queryKey: ["dashboard"],
    queryFn: async () => (await api.get("/dashboard/summary")).data,
    refetchInterval: 60_000,
  });

  const { data: brief, refetch: refetchBrief } = useQuery({
    queryKey: ["daily-brief"],
    queryFn: async () => (await api.get("/intel/brief")).data,
    staleTime: 60_000,
    // Re-poll every 3 minutes so drift-triggered auto-regeneration is picked up.
    refetchInterval: 180_000,
  });

  const regen = useMutation({
    mutationFn: async () => (await api.post("/intel/brief/regenerate")).data,
    onSuccess: () => { setRegenerating(false); refetchBrief(); toast.success("Daily brief regenerated"); },
    onError: (e) => { setRegenerating(false);
      toast.error(formatApiError(e.response?.data?.detail) || "Regenerate failed"); },
  });

  const cards = queue?.cards || [];

  return (
    <div className="space-y-8" data-testid="dashboard">
      <FirstRunChecklist />
      {/* Trade of the Day — v2.6 hero */}
      <TradeOfTheDay testid="trade-of-day" />

      {/* KPI cards row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: "Active markets", value: summary?.cards?.active_markets, testid: "card-active-markets", tip: "Polymarket markets currently being tracked and synced." },
          { label: "Tracked traders", value: summary?.cards?.tracked_traders, testid: "card-tracked-traders", tip: "Number of Polymarket wallets whose positions we reconstruct." },
          { label: "Live signals · 24h", value: summary?.cards?.signals_24h, testid: "card-signals-24h", tip: "New actionable signals generated in the last 24 hours on live/active markets only." },
          { label: "Live high confidence", value: summary?.cards?.high_confidence_signals, testid: "card-high-conf", tip: "Live active-market signals with High confidence based on trader count, data completeness and consensus concentration." },
        ].map((c) => (
          <div key={c.label} className="border border-zinc-800 rounded-md p-3" data-testid={c.testid} title={c.tip}>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500">{c.label}</div>
            <div className="text-xl font-mono-num text-zinc-100 mt-1">{fmtNum(c.value)}</div>
          </div>
        ))}
      </div>

      {/* Daily brief + Data health + Movers */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 sm:gap-6">
        <SectionCard title="Today's AI Brief" icon={Sparkles} testid="daily-brief"
          cta={<div className="flex flex-wrap items-center gap-2">
                 <AIQuotaBadge endpoint="daily_brief_manual" compact />
                 <Button size="sm" onClick={() => { setRegenerating(true); regen.mutate(); }}
                       disabled={regenerating || regen.isPending}
                       data-testid="btn-regenerate-brief"
                       className="h-7 text-xs bg-zinc-800 hover:bg-zinc-700 text-zinc-100">
                   <RefreshCw size={11} className={`mr-1 ${regenerating || regen.isPending ? "animate-spin" : ""}`} />
                   Regenerate
                 </Button>
               </div>}>
          {!brief ? (
            <div className="text-xs text-zinc-500 py-2">Loading brief…</div>
          ) : (
            <>
              {brief.fallback && (
                <div className="text-[10px] text-amber-300 mb-2">
                  provider unavailable · deterministic fallback
                </div>
              )}
              <pre className="whitespace-pre-wrap font-sans text-xs text-zinc-300 leading-relaxed max-h-72 overflow-auto"
                   data-testid="daily-brief-text">{brief.text}</pre>
              {brief.recommended_investigation?.length > 0 && (
                <div className="mt-2 pt-2 border-t border-zinc-800/70">
                  <div className="text-[9px] uppercase tracking-widest text-blue-300/70 mb-1">
                    Recommended Investigation
                  </div>
                  <ul className="text-xs text-zinc-300 space-y-0.5" data-testid="daily-brief-recommended">
                    {brief.recommended_investigation.slice(0, 4).map((r, i) => (
                      <li key={i}>→ {r}</li>
                    ))}
                  </ul>
                </div>
              )}
              <div className="mt-2 pt-2 border-t border-zinc-800 text-[10px] text-zinc-500 font-mono-num flex items-center justify-between">
                <span data-testid="daily-brief-generated-at">
                  Generated {_briefAgeLabel(brief.age_seconds, brief.generated_at)}
                  {" · "}{brief.evidence_ids?.length ?? 0} evidence records
                </span>
                {brief.is_stale && (
                  <span className="text-amber-300" data-testid="daily-brief-stale-flag">stale · refreshing</span>
                )}
              </div>
            </>
          )}
        </SectionCard>

        <SectionCard title="Largest movers" icon={Radar} testid="section-movers"
          cta={<Link to="/app/markets" className="text-[10px] uppercase tracking-widest text-zinc-500 hover:text-zinc-100">All markets →</Link>}>
          <ul className="divide-y divide-zinc-800/70">
            {(summary?.largest_movers || []).slice(0, 6).map((m) => (
              <li key={m.polymarket_market_id}>
                <Link to={`/app/markets/${m.polymarket_market_id}`}
                      className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between py-1.5 hover:bg-zinc-900/40 px-2 rounded-sm">
                  <div className="min-w-0">
                    <div className="text-sm text-zinc-100 truncate">{m.title}</div>
                    <div className="text-[10px] text-zinc-500">{m.category}</div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="font-mono-num text-xs text-zinc-200">{fmtUSD(m.volume_24h)}</div>
                    <div className="text-[10px] text-zinc-500">24h vol</div>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        </SectionCard>

        <SectionCard title="Data health" icon={Activity} testid="section-health">
          <div className="space-y-2 text-sm">
            {["prices", "markets", "traders"].map((k) => (
              <div key={k} className="flex items-center justify-between">
                <span className="text-zinc-400 capitalize">{k}</span>
                <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm ${
                  summary?.freshness?.[k] === "live" ? "bg-emerald-500/15 text-emerald-400" :
                  summary?.freshness?.[k] === "delayed" ? "bg-amber-500/15 text-amber-300" :
                  "bg-zinc-500/15 text-zinc-400"
                }`}>
                  {summary?.freshness?.[k] || "unknown"}
                </span>
              </div>
            ))}
          </div>
        </SectionCard>
      </div>

      {/* Hero — Investigation Queue */}
      <header>
        <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">
          Investigate First
        </div>
        <h1 className="font-heading text-3xl md:text-4xl font-bold text-zinc-100 tracking-tight mt-1">
          What deserves your attention today.
        </h1>
        <p className="text-sm text-zinc-400 mt-2 max-w-2xl">
          A ranked queue of the highest-signal opportunities right now, with confidence,
          remaining edge, and an estimated research time so you know where to focus.
        </p>
      </header>

      <section data-testid="investigation-queue">
        {queueLoading && (
          <div className="text-sm text-zinc-500">Assembling queue…</div>
        )}
        {!queueLoading && cards.length === 0 && (
          <div className="border border-zinc-800 rounded-md p-8 text-center">
            <Compass size={22} className="mx-auto text-zinc-500" />
            <p className="mt-2 text-sm text-zinc-400">
              No active opportunities right now. Check back after the next sync.
            </p>
          </div>
        )}
        <div className="grid gap-3 stagger-in">
          {cards.map((c, i) => (
            <InvestigationCard key={c.signal_id} card={c} rank={i + 1} />
          ))}
        </div>
      </section>
    </div>
  );
}
